<script setup lang="ts">
import BPLogoIcon from "@/assets/svg/bp-logo-white.svg?component";
import IBGLogoBlackIcon from "@/assets/svg/ibg-logo-black.svg?component";
import BarsIcon from "@/assets/svg/bars.svg?component";
import XMarkIcon from "@/assets/svg/xmark.svg?component";
import NavMenu from "@/stories/navigations/NavMenu/NavMenu.vue";
import { useBreakpoints } from "@vueuse/core";
import {
  BREAKPOINTS,
  IS_BP_MANAGER,
  IS_IBG_MANAGER,
  MOBILE_NAV_HEIGHT,
} from "@/consts";
import { computed, ref } from "vue";
import MobileNavMenu from "@/stories/navigations/NavMenu/MobileNavMenu.vue";
import Line from "@/stories/layout/Line/Line.vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/authStore";
import InstitutionSwitcher from "@/stories/navigations/InstitutionSwitcher/InstitutionSwitcher.vue";
import LoadingDialog from "@/stories/dialogs/LoadingDialog.vue";
import LoggedInAs from "@/stories/navigations/NavMenu/LoggedInAs.vue";
import SVG from "@/stories/displays/SVG/SVG.vue";
import NavigationDotMenu from "@/components/layouts/navigations/NavigationDotMenu.vue";
import { useNavigationStore } from "@/stores/navigation.module";
import TabletNavigationLogos from "@/components/layouts/navigations/tablet/TabletNavigationLogos.vue";
import TabletSupportLink from "@/components/layouts/navigations/tablet/TabletSupportLink.vue";
import { useContentHeight } from "@/composables/useContentHeight";

const authStore = useAuthStore();
const breakpoints = useBreakpoints(BREAKPOINTS);
const isTablet = breakpoints.greaterOrEqual("tablet");
const navigationStore = useNavigationStore();

const loading = ref(false);
const navigationScrollerRef = ref();

const handleLogout = async () => {
  loading.value = true;
  await authStore.logout();
};

const navigationScrollerHeight = useContentHeight(navigationScrollerRef);

const supportLinkPosition = computed<"scroller" | "standalone">(() => {
  if (navigationScrollerHeight.dynamicHeight.value < 800) {
    return "scroller";
  }

  return "standalone";
});
</script>

<template>
  <LoadingDialog v-if="loading" />

  <nav
    :class="[
      'bg-darkOcean700 left-0 fixed top-0 h-dvh flex flex-col justify-between text-white z-10',
      'tablet:w-[5rem]',
      'desktop:w-[13.625rem]',
      'print:hidden',
    ]"
    id="mobile-navigation"
    v-if="(authStore.isAuthenticated || authStore.loggingOut) && isTablet"
  >
    <TabletNavigationLogos />

    <div
      ref="navigationScrollerRef"
      :class="[
        'flex-grow flex flex-col items-center justify-center gap-1 overflow-y-auto navigation-scroller pb-4',
        'tablet:px-1',
        'desktop:px-4 desktop:justify-start',
        'short:justify-start',
      ]"
    >
      <NavMenu
        v-for="link of navigationStore.permittedMenus"
        :key="link.value"
        :icon="link.icon"
        :title="link.title"
        :to="link.value"
        :active="navigationStore.currentMenu?.value === link.value"
      />
      <TabletSupportLink v-if="supportLinkPosition === 'scroller'" />
    </div>

    <TabletSupportLink
      v-if="supportLinkPosition === 'standalone'"
      class="px-2 pb-4"
    />

    <div class="desktop:px-4 pb-4 flex flex-col justify-center items-center">
      <div class="bg-neutral500 opacity-20 mb-2 h-[1px] w-full"></div>
      <NavigationDotMenu @logout="handleLogout" />
    </div>
  </nav>

  <nav
    v-if="(authStore.isAuthenticated || authStore.loggingOut) && !isTablet"
    class="bg-white flex items-center justify-between px-4 border-b border-neutral300 relative"
    :style="{ height: MOBILE_NAV_HEIGHT }"
  >
    <RouterLink to="/">
      <BPLogoIcon v-if="IS_BP_MANAGER" width="8.8125rem" height="1.5625rem" />
      <IBGLogoBlackIcon
        v-if="IS_IBG_MANAGER"
        width="8.125rem"
        height="2.125rem"
      />
    </RouterLink>
    <button
      type="button"
      @click="
        navigationStore.isMobileMenuActive = !navigationStore.isMobileMenuActive
      "
      class="transition-all"
    >
      <BarsIcon
        v-if="!navigationStore.isMobileMenuActive"
        class="animate-in fade-in"
        width="2rem"
        height="2rem"
      />
      <XMarkIcon
        v-if="navigationStore.isMobileMenuActive"
        class="animate-in fade-in"
        width="2rem"
        height="2rem"
      />
    </button>
    <div
      v-if="navigationStore.isMobileMenuActive"
      :class="`fixed left-0 w-full bg-white py-10 px-4 animate-in fade-in z-50 overflow-auto`"
      :style="{
        top: MOBILE_NAV_HEIGHT,
        height: `calc(100dvh - ${MOBILE_NAV_HEIGHT})`,
      }"
    >
      <div class="flex flex-col gap-8 pb-3">
        <InstitutionSwitcher
          v-if="
            ((navigationStore.isMobileMenuActive &&
              authStore.profile?.institutions?.length) ||
              0) > 1
          "
        ></InstitutionSwitcher>

        <MobileNavMenu
          v-for="link of navigationStore.permittedMenus"
          :key="link.value"
          :icon="link.icon"
          :title="link.title"
          :to="link.value"
          :active="navigationStore.currentMenu?.value === link.value"
          @click="navigationStore.isMobileMenuActive = false"
        />
      </div>

      <LoggedInAs class="mt-4" is-mobile />

      <Line padding="lg" />
      <div class="pt-3">
        <MobileNavMenu
          v-if="authStore.isSupport && IS_IBG_MANAGER"
          class="mb-6"
          key="admin"
          icon="fas-lock"
          :title="$t('global.admin')"
          to="/admin"
          :active="navigationStore.isAdminRoute"
          @click="navigationStore.isMobileMenuActive = false"
        />

        <button
          type="button"
          class="h600 flex items-center gap-3"
          @click="handleLogout"
        >
          <SVG icon="far-sign-out" width="2rem" height="2rem"></SVG>
          {{ $t("main.logout") }}
        </button>
      </div>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.navigation-scroller {
  scrollbar-width: thin; /* Firefox: slim scrollbar */
  scrollbar-color: #769297 #194a51; /* thumb | track */
}

/* Chrome, Edge, Safari */
.navigation-scroller::-webkit-scrollbar {
  width: 0.6875rem; /* total scrollbar container width */
}

.navigation-scroller::-webkit-scrollbar-thumb {
  background-color: #769297; /* scrollbar thumb color */
  border-radius: 0.5rem; /* rounded corners */
  border: 2px solid #194a51; /* creates "padding" so thumb looks 0.4375rem */
}

.navigation-scroller::-webkit-scrollbar-track {
  background: #194a51; /* track background color */
}
</style>
